import { apiError } from "../../../../../../utils/formErrors";
import { ensureOrgEmployees } from "../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, Select, MenuItem, CircularProgress, Autocomplete, TextField } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router";
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import TypeBtn from "../../../../../../component/AddRemoveButton";
import { WizardCard, FormSection, FieldLabel } from "../../../../../../component/wizard";
import { selectSx } from "../../../../../../component/listing";

import { createOffCycleRun } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { MONTHS, money } from "../payrollUtils";

import { pressable } from "../../../../../../component/pressableProps";
const now = new Date();
const emptyLine = () => ({ employee: null, adjustments: [{ name: "", amount: "", type: "EARNING" }] });

const OffCycleRun = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [employees, setEmployees] = useState([]);
  const [label, setLabel] = useState("");
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const list = await ensureOrgEmployees().catch(() => null);
      setEmployees((list || []).map((e) => ({ id: e._id, name: `${e.fname || ""} ${e.lname || ""}`.trim() })));
    })();
  }, []);

  const net = useMemo(() => lines.reduce((t, l) => t + (l.adjustments || []).reduce((s, a) => {
    const v = Number(a.amount) || 0; return s + (a.type === "DEDUCTION" ? -v : v);
  }, 0), 0), [lines]);

  const setLine = (i, patch) => setLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const setAdj = (li, ai, patch) => setLines((p) => p.map((l, idx) => idx === li
    ? { ...l, adjustments: l.adjustments.map((a, aj) => (aj === ai ? { ...a, ...patch } : a)) } : l));

  const submit = async () => {
    const payloadLines = lines
      .filter((l) => l.employee && l.adjustments.some((a) => a.name && a.amount))
      .map((l) => ({ employee: l.employee.id, adjustments: l.adjustments.filter((a) => a.name && a.amount).map((a) => ({ name: a.name, amount: Number(a.amount), type: a.type })) }));
    if (payloadLines.length === 0) { notify.error("Add at least one employee with an adjustment"); return; }
    setSaving(true);
    try {
      const res = await createOffCycleRun({ month, year, label: label.trim() || "Off-cycle adjustment", lines: payloadLines });
      if (res?.success === false) throw apiError(res, "Could not create off-cycle run");
      notify.success("Off-cycle run created");
      navigate(`/app/settings/payroll/run/${res.data._id}`);
    } catch (e) { notify.error(e?.message || "Could not create off-cycle run"); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Payroll"} breadcrumbs={[{ label: "Runs", href: "/app/settings/payroll/runs" }, { label: "Off-cycle" }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>Off-cycle Adjustment Run</Box>
          <BtnSecondary to="/app/settings/payroll/runs" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        <FormSection title="Details" description="Arrears, bonuses or recoveries posted alongside the regular run — the regular payroll for the period is untouched.">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Box sx={{ flex: 1.4 }}><FieldLabel>Label</FieldLabel><FormField type="text" value={label} onChange={(e) => setLabel(e.target.value)} fullwidth placeholder="e.g. April increment arrears" /></Box>
            <Box sx={{ flex: 1 }}><FieldLabel>Month</FieldLabel><Select value={month} onChange={(e) => setMonth(e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>{MONTHS.map((mn, i) => <MenuItem key={mn} value={i}>{mn}</MenuItem>)}</Select></Box>
            <Box sx={{ flex: 0.7 }}><FieldLabel>Year</FieldLabel><Select value={year} onChange={(e) => setYear(e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></Box>
          </Stack>
        </FormSection>

        <FormSection title="Employees & adjustments">
          <Stack spacing={1.5}>
            {lines.map((l, li) => (
              <Box key={li} sx={{ p: "1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: "1rem" }}>
                  <Box sx={{ flex: 1 }}>
                    <FieldLabel>Employee</FieldLabel>
                    <Autocomplete options={employees} getOptionLabel={(o) => o?.name || ""} size="small" value={l.employee}
                      onChange={(e, v) => setLine(li, { employee: v })}
                      sx={{ "& .MuiInputBase-root": { borderRadius: "10px" } }} renderInput={(p) => <TextField {...p} placeholder="Select employee" />} />
                  </Box>
                  {lines.length > 1 && <Box {...pressable(() => setLines((p) => p.filter((_, idx) => idx !== li)), "Remove")} sx={{ cursor: "pointer", color: tokens.danger, pt: "1.25rem" }}><DeleteOutlineIcon /></Box>}
                </Stack>
                <Stack spacing={1}>
                  {l.adjustments.map((a, ai) => (
                    <Stack key={ai} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                      <Box sx={{ flex: 1.5 }}><FormField type="text" value={a.name} onChange={(e) => setAdj(li, ai, { name: e.target.value })} fullwidth placeholder="Adjustment name" /></Box>
                      <Box sx={{ flex: 1 }}><FormField type="number" value={a.amount} onChange={(e) => setAdj(li, ai, { amount: e.target.value })} fullwidth placeholder="Amount" /></Box>
                      <Select value={a.type} onChange={(e) => setAdj(li, ai, { type: e.target.value })} size="small" sx={{ ...selectSx, width: "9rem" }}>
                        <MenuItem value="EARNING">Earning</MenuItem>
                        <MenuItem value="DEDUCTION">Deduction</MenuItem>
                      </Select>
                      {l.adjustments.length > 1 && <Box {...pressable(() => setLine(li, { adjustments: l.adjustments.filter((_, aj) => aj !== ai) }), "Remove")} sx={{ cursor: "pointer", color: tokens.danger }}><DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} /></Box>}
                    </Stack>
                  ))}
                  <Box><TypeBtn type="add" onClick={() => setLine(li, { adjustments: [...l.adjustments, { name: "", amount: "", type: "EARNING" }] })}>Add adjustment</TypeBtn></Box>
                </Stack>
              </Box>
            ))}
            <Box><TypeBtn type="add" onClick={() => setLines([...lines, emptyLine()])}>Add employee</TypeBtn></Box>
          </Stack>
        </FormSection>

        <Box sx={{ background: tokens.surfaceSubtle, border: `1px solid ${tokens.border}`, borderRadius: "12px", p: "1.25rem", mb: "1.5rem", textAlign: "center" }}>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Net adjustment</Box>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, color: net < 0 ? tokens.danger : tokens.successText }}>{money(net)}</Box>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <BtnSecondary to="/app/settings/payroll/runs" sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? <CircularProgress size={"1.25rem"} /> : "Create Off-cycle Run"}</BtnPrimary>
        </Stack>
      </WizardCard>
    </Box>
  );
};

export default OffCycleRun;
